import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowRightLeft,
  BadgeDollarSign,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  CreditCard,
  FileDown,
  FileWarning,
  Filter,
  ListChecks,
  Receipt,
  RefreshCcw,
  Search,
  SearchCheck,
  Settings,
  Split,
  StickyNote,
  Store,
  UserX,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import Pagination from '../components/Pagination';
import TableZoomControls, { useTableZoom } from '../components/TableZoomControls';
import TipTransactionsGuide from '../components/TipTransactionsGuide';
import { getFeeSourceLabel } from '../constants/feeSourceLabels';
import '../pages/TipTransactions.css';
import { tenantService, tipTransactionService, venueService } from '../services/api';

const TERMINAL_FAILURE_OUTCOMES = ['CONFIRM_FAILED', 'COLLECT_DECLINED', 'COLLECT_FAILED'];
const TERMINAL_WARNING_OUTCOMES = [...TERMINAL_FAILURE_OUTCOMES];
const TERMINAL_NON_FINAL_OUTCOMES = ['CREATED', 'RETRIEVED', 'COLLECT_STARTED', 'CONFIRM_STARTED'];
const TERMINAL_SUCCESS_OUTCOMES = ['SUCCEEDED', 'COLLECT_COMPLETED'];
const PENDING_PAYMENT_STATUSES = ['processing', 'pending'];
const DATE_PRESET_LABELS = {
  today: 'Oggi',
  yesterday: 'Ieri',
  '7d': 'Ultimi 7 giorni',
  '30d': 'Ultimi 30 giorni',
  this_month: 'Questo mese',
  last_month: 'Mese scorso',
  custom: 'Periodo personalizzato',
};
const PAYMENT_STATUS_LABELS = {
  COLLECTED: 'Riuscite',
  NOT_COLLECTED: 'Non incassate',
  PENDING: 'In attesa',
  MISSING_TRANSFER: 'Transfer mancante',
};
const ActiveFilterIndicator = () => (
  <div className="summary-filter-indicator">Attivo</div>
);
const SummaryLabel = ({ icon: Icon, children }) => (
  <div className="summary-label summary-label--with-icon">
    <Icon size={15} aria-hidden="true" />
    <span>{children}</span>
  </div>
);
const TransactionColumnLabel = ({ icon: Icon, children }) => (
  <span className="transaction-column-label">
    <Icon size={14} aria-hidden="true" />
    <span>{children}</span>
  </span>
);
const getDefaultDateRange = () => {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const toIso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = toIso(now);
  return { from: today, to: today };
};

const EMPTY_FILTERS = {
  from: '',
  to: '',
  tenantId: '',
  venueId: '',
  paymentStatus: '',
  recipientMode: '',
  distributionMode: '',
  cardCountry: '',
  terminalOutcome: '',
  collectionChannel: '',
  q: ''
};

const TipTransactions = () => {
  const tableZoom = useTableZoom();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    ...getDefaultDateRange(),
    paymentStatus: 'COLLECTED'
  });
  const [datePreset, setDatePreset] = useState('today');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Tenants and Venues for filters
  const [tenants, setTenants] = useState([]);
  const [venues, setVenues] = useState([]);
  const [allVenues, setAllVenues] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Summary metrics
  const [summary, setSummary] = useState(null);

  // Quick filters (client-side)
  const [paymentProblemFilter, setPaymentProblemFilter] = useState(false);
  const [billingProblemFilter, setBillingProblemFilter] = useState(false);
  const [customerAbortFilter, setCustomerAbortFilter] = useState(false);
  const [toReviewFilter, setToReviewFilter] = useState(false);

  // Export
  const [exportLoading, setExportLoading] = useState(false);

  // Kebab menu
  const [openKebabId, setOpenKebabId] = useState(null);
  const kebabRef = useRef(null);

  // Copy feedback
  const [copyFeedback, setCopyFeedback] = useState(null);

  // Detail modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const activeFilterCount = [
    datePreset !== 'all',
    filters.tenantId,
    filters.venueId,
    filters.paymentStatus,
    filters.recipientMode,
    filters.terminalOutcome,
    filters.cardCountry,
    filters.distributionMode,
    filters.collectionChannel,
    paymentProblemFilter,
    billingProblemFilter,
    customerAbortFilter,
    toReviewFilter,
  ].filter(Boolean).length;

  useEffect(() => {
    loadTenantsAndVenues();
  }, []);

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line
  }, [currentPage, filters, pageSize]);

  // Close kebab on outside click
  useEffect(() => {
    if (!openKebabId) return;
    const handleOutside = (e) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target)) {
        setOpenKebabId(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [openKebabId]);

  const loadTenantsAndVenues = async () => {
    try {
      const [tenantsData, venuesData] = await Promise.all([
        tenantService.getTenants(),
        venueService.getVenues()
      ]);
      setTenants(tenantsData.data || tenantsData || []);
      setAllVenues(venuesData.venues || venuesData.data || []);
      setVenues(venuesData.venues || venuesData.data || []);
    } catch (err) {
      console.error('Failed to load tenants/venues:', err);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await tipTransactionService.getTransactions({
        ...filters,
        page: currentPage,
        pageSize,
        summary: true,
        summaryIgnoreStatusFilters: true
      });

      setTransactions(data.data);
      setTotalItems(data.pagination.total);
      setTotalPages(data.pagination.totalPages);

      if (data.summary) {
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Failed to load tip transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'from' || key === 'to') setDatePreset('custom');
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);

    // When tenant changes, filter venues
    if (key === 'tenantId') {
      if (value) {
        const filteredVenues = allVenues.filter(v => v.tenantId === value);
        setVenues(filteredVenues);
        // Reset venue selection if it doesn't belong to selected tenant
        if (filters.venueId && !filteredVenues.find(v => v.id === filters.venueId)) {
          setFilters(prev => ({ ...prev, venueId: '' }));
        }
      } else {
        setVenues(allVenues);
      }
    }
  };

  const resetFilters = () => {
    setFilters({
      ...EMPTY_FILTERS,
      ...getDefaultDateRange(),
      paymentStatus: 'COLLECTED'
    });
    setDatePreset('today');
    setVenues(allVenues);
    setCurrentPage(1);
    setPaymentProblemFilter(false);
    setBillingProblemFilter(false);
    setCustomerAbortFilter(false);
    setToReviewFilter(false);
  };

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    setCurrentPage(1);
    if (preset === 'custom') return;
    if (preset === 'all') {
      setFilters(prev => ({ ...prev, from: '', to: '' }));
      return;
    }
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const toIso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    let from = '';
    let to = '';
    if (preset === 'today') {
      from = toIso(now); to = toIso(now);
    } else if (preset === 'yesterday') {
      const d = new Date(now); d.setDate(d.getDate() - 1);
      from = toIso(d); to = toIso(d);
    } else if (preset === '7d') {
      const d = new Date(now); d.setDate(d.getDate() - 6);
      from = toIso(d); to = toIso(now);
    } else if (preset === '30d') {
      const d = new Date(now); d.setDate(d.getDate() - 29);
      from = toIso(d); to = toIso(now);
    } else if (preset === 'this_month') {
      from = toIso(new Date(now.getFullYear(), now.getMonth(), 1));
      to = toIso(now);
    } else if (preset === 'last_month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      from = toIso(firstDay); to = toIso(lastDay);
    }
    setFilters(prev => ({ ...prev, from, to }));
  };

  const handlePageSizeChange = (size) => {
    setPageSize(parseInt(size, 10));
    setCurrentPage(1);
  };

  const derivePaymentStatus = useCallback((tx) => {
    const status = String(tx?.status || '').toLowerCase();
    const transferCount = tx?.tipTransfers?.length || 0;

    if (status === 'succeeded' && transferCount > 0) return 'COLLECTED';
    if (status === 'succeeded' && transferCount === 0) return 'MISSING_TRANSFER';
    if (PENDING_PAYMENT_STATUSES.includes(status)) return 'PENDING';
    if (status.startsWith('requires_') || status === 'failed' || status === 'payment_failed' || status === 'canceled') {
      return 'NOT_COLLECTED';
    }

    return 'UNKNOWN';
  }, []);

  const getPaymentStatusBadge = useCallback((tx) => {
    const paymentStatus = derivePaymentStatus(tx);
    const stripeStatus = tx?.status || 'N/D';
    const terminalOutcome = tx?.terminalOutcome || 'N/D';

    const config = {
      COLLECTED: { label: 'Incassato', bg: '#28a745', color: 'white' },
      MISSING_TRANSFER: { label: 'Transfer mancante', bg: '#fd7e14', color: 'white' },
      PENDING: { label: 'In attesa', bg: '#17a2b8', color: 'white' },
      NOT_COLLECTED: { label: 'Non incassato', bg: '#dc3545', color: 'white' },
      UNKNOWN: { label: 'Non incassato', bg: '#6c757d', color: 'white' },
    };

    const c = config[paymentStatus] || config.UNKNOWN;
    return (
      <span
        className="tx-compact-badge"
        style={{ backgroundColor: c.bg, color: c.color }}
        title={`Stripe status: ${stripeStatus}\nTerminal outcome: ${terminalOutcome}`}
      >
        {c.label}
      </span>
    );
  }, [derivePaymentStatus]);

  const deriveTransferState = useCallback((tx) => {
    const paymentStatus = derivePaymentStatus(tx);
    const transferCount = tx?.tipTransfers?.length || 0;

    if (paymentStatus === 'MISSING_TRANSFER') {
      return { key: 'MISSING', label: 'Mancante', bg: '#fd7e14', color: 'white', marker: '' };
    }

    if (paymentStatus === 'COLLECTED') {
      return {
        key: 'COUNT',
        label: `${transferCount} transfer`,
        bg: '#007bff',
        color: 'white',
        marker: '',
        title: `Transfer registrati: ${transferCount}`
      };
    }

    return { key: 'NA', label: '—', bg: '#f1f3f5', color: '#6c757d', marker: '' };
  }, [derivePaymentStatus]);

  const deriveRecipientState = useCallback((tx) => {
    const mode = tx?.recipientModeSummary || (tx?.selectedWorkerSummary ? 'NAMED_SINGLE' : 'STAFF');
    if (mode === 'NAMED_SINGLE') {
      return {
        key: 'NAMED_SINGLE',
        label: 'Nominale',
        bg: '#6f42c1',
        color: 'white',
        detail: tx?.selectedWorkerSummary || null,
      };
    }

    return {
      key: 'STAFF',
      label: 'Staff',
      bg: '#495057',
      color: 'white',
      detail: null,
    };
  }, []);

  const deriveDistributionState = useCallback((tx) => {
    const mode = tx?.distributionSummary || 'STANDARD';

    if (mode === 'WORKER_PERCENTAGE') {
      return { key: 'WORKER_PERCENTAGE', label: 'Percettore %', bg: '#8b5cf6', color: 'white' };
    }
    if (mode === 'TEAM_PERCENTAGE') {
      return { key: 'TEAM_PERCENTAGE', label: 'Team %', bg: '#f59e0b', color: 'white' };
    }

    return { key: 'STANDARD', label: 'Standard', bg: '#6c757d', color: 'white' };
  }, []);

  const deriveChannelState = useCallback((tx) => {
    const channel = tx?.collectionChannel || 'KIOSK_TERMINAL';
    if (channel === 'TIP_LINK_WEB') {
      return { key: 'TIP_LINK_WEB', label: 'Tip Link', bg: '#C21F7A', color: 'white', icon: '🔗' };
    }
    return { key: 'KIOSK_TERMINAL', label: 'Totem', bg: '#343a40', color: 'white', icon: '🖥️' };
  }, []);

  const deriveBillingState = useCallback((tx) => {
    const paymentStatus = derivePaymentStatus(tx);
    const totalTransfers = tx?.tipTransfers?.length || 0;
    const invoicedCount = tx?.tipTransfers?.filter(t => t.workerInvoiceId).length || 0;
    const hasInvoiceError = tx?.tipTransfers?.some(t =>
      t.workerInvoice?.acubeInvoiceStatus === 'error' ||
      t.workerInvoice?.acubeInvoiceStatus === 'rejected'
    );

    if (paymentStatus === 'NOT_COLLECTED' || paymentStatus === 'PENDING' || paymentStatus === 'UNKNOWN') {
      return { key: 'NA', label: '—', bg: '#f1f3f5', color: '#6c757d' };
    }

    if (hasInvoiceError) {
      return { key: 'ERROR', label: 'Errore fattura', bg: '#fd7e14', color: 'white' };
    }

    if (totalTransfers === 0) {
      return { key: 'NA', label: '—', bg: '#f1f3f5', color: '#6c757d' };
    }

    if (invoicedCount === totalTransfers) {
      return { key: 'INVOICED', label: 'Fatturata', bg: '#28a745', color: 'white' };
    }

    return { key: 'NOT_INVOICED', label: 'Non fatturata', bg: '#dee2e6', color: '#495057' };
  }, [derivePaymentStatus]);

  const getLastPaymentErrorDetail = useCallback((tx) => {
    const lastError = tx?.metadata?.last_payment_error;
    if (!lastError) return null;
    return {
      message: lastError.message || null,
      declineCode: lastError.decline_code || null,
      code: lastError.code || null,
      cardBrand: lastError.payment_method?.card?.brand || null,
      cardLast4: lastError.payment_method?.card?.last4 || null,
    };
  }, []);

  const deriveNoteState = useCallback((tx) => {
    const status = String(tx?.status || '').toLowerCase();
    const transferCount = tx?.tipTransfers?.length || 0;
    const terminalOutcome = tx?.terminalOutcome;
    // Errore Stripe (es. carta rifiutata, fondi insufficienti) salvato dal webhook
    // payment_intent.payment_failed: è channel-agnostic (kiosk e tip link entrambi passano
    // dallo stesso handler backend), quindi lo esponiamo come dettaglio/tooltip indipendentemente
    // dal canale, quando disponibile.
    const lastPaymentError = getLastPaymentErrorDetail(tx);
    const errorDetailText = lastPaymentError
      ? [lastPaymentError.message, lastPaymentError.declineCode ? `(${lastPaymentError.declineCode})` : null].filter(Boolean).join(' ')
      : null;
    // Il Tip Link web checkout non passa mai da un terminale fisico: gli outcome/note
    // "terminale" (COLLECT_TIMEOUT, COLLECT_CANCELED, outcome mancante, ecc.) non hanno senso
    // per questo canale e vanno sempre ignorati per evitare falsi allarmi (es. "Outcome
    // terminale mancante" su ogni pagamento web riuscito, che finiva anche tra i "da verificare").
    const isWebChannel = (tx?.collectionChannel || 'KIOSK_TERMINAL') === 'TIP_LINK_WEB';

    if (status !== 'succeeded') {
      if (!isWebChannel && terminalOutcome === 'COLLECT_TIMEOUT') {
        return { key: 'CUSTOMER_TIMEOUT', label: 'Timeout - carta non presentata', bg: '#17a2b8', color: 'white' };
      }
      if (!isWebChannel && terminalOutcome === 'COLLECT_CANCELED') {
        return { key: 'CUSTOMER_CANCELED', label: 'Annullato dal cliente', bg: '#17a2b8', color: 'white' };
      }
      if (status === 'requires_payment_method') {
        return {
          key: 'PAYMENT_INCOMPLETE',
          label: isWebChannel ? 'Checkout non completato dal cliente' : 'Pagamento non completato',
          bg: '#dc3545',
          color: 'white',
          detail: errorDetailText,
        };
      }
      if (status === 'canceled') {
        return { key: 'PAYMENT_CANCELED', label: 'Pagamento annullato', bg: '#dc3545', color: 'white' };
      }
      if (status === 'failed' || status === 'payment_failed') {
        return { key: 'PAYMENT_FAILED', label: 'Pagamento fallito', bg: '#dc3545', color: 'white', detail: errorDetailText };
      }
      if (status === 'requires_action') {
        return { key: 'CUSTOMER_ACTION', label: 'Azione cliente richiesta', bg: '#dc3545', color: 'white' };
      }
      if (status === 'requires_confirmation') {
        return { key: 'PAYMENT_UNCONFIRMED', label: 'Pagamento non confermato', bg: '#dc3545', color: 'white' };
      }
      if (status.startsWith('requires_')) {
        return { key: 'PAYMENT_INCOMPLETE', label: 'Pagamento non completato', bg: '#dc3545', color: 'white' };
      }
      if (status === 'processing' || status === 'pending') {
        return { key: 'STRIPE_PENDING', label: 'In attesa conferma Stripe', bg: '#17a2b8', color: 'white' };
      }

      return { key: 'PAYMENT_UNKNOWN', label: 'Stato pagamento da verificare', bg: '#fd7e14', color: 'white' };
    }

    if (status === 'succeeded' && transferCount === 0) {
      return { key: 'MISSING_TRANSFER_NOTE', label: 'Pagamento incassato, transfer non registrato', bg: '#fd7e14', color: 'white' };
    }

    // Da qui in poi sono tutte note specifiche del terminale fisico (kiosk): il canale web
    // non le riguarda mai, quindi un pagamento web riuscito con transfer è semplicemente "pulito".
    if (isWebChannel) {
      return { key: 'NONE', label: '—', bg: '#f1f3f5', color: '#6c757d' };
    }

    if (!terminalOutcome) {
      return { key: 'TERMINAL_MISSING', label: 'Outcome terminale mancante', bg: '#ffc107', color: '#212529' };
    }

    if (TERMINAL_WARNING_OUTCOMES.includes(terminalOutcome)) {
      return { key: 'TERMINAL_WARNING', label: 'Terminale da verificare', bg: '#fd7e14', color: 'white' };
    }

    if (TERMINAL_NON_FINAL_OUTCOMES.includes(terminalOutcome)) {
      return { key: 'TERMINAL_NON_FINAL', label: 'Outcome terminale non finale', bg: '#ffc107', color: '#212529' };
    }

    if (!TERMINAL_SUCCESS_OUTCOMES.includes(terminalOutcome)) {
      return { key: 'TERMINAL_WARNING', label: 'Terminale da verificare', bg: '#fd7e14', color: 'white' };
    }

    return { key: 'NONE', label: '—', bg: '#f1f3f5', color: '#6c757d' };
  }, [getLastPaymentErrorDetail]);

  const isPaymentProblem = useCallback((tx) => {
    const paymentStatus = derivePaymentStatus(tx);
    const noteKey = deriveNoteState(tx).key;
    if (noteKey === 'CUSTOMER_TIMEOUT' || noteKey === 'CUSTOMER_CANCELED') return false;
    return paymentStatus === 'NOT_COLLECTED' || paymentStatus === 'MISSING_TRANSFER' || paymentStatus === 'UNKNOWN';
  }, [derivePaymentStatus, deriveNoteState]);

  const isBillingProblem = useCallback((tx) => {
    const billingState = deriveBillingState(tx);
    return billingState.key === 'NOT_INVOICED' || billingState.key === 'ERROR';
  }, [deriveBillingState]);

  const isToReview = useCallback((tx) => {
    const noteState = deriveNoteState(tx);
    return ['TERMINAL_WARNING', 'TERMINAL_NON_FINAL', 'TERMINAL_MISSING', 'PAYMENT_UNKNOWN'].includes(noteState.key);
  }, [deriveNoteState]);

  const isCustomerAbortCase = useCallback((tx) => {
    const noteKey = deriveNoteState(tx).key;
    return noteKey === 'CUSTOMER_TIMEOUT' || noteKey === 'CUSTOMER_CANCELED';
  }, [deriveNoteState]);

  const getNoteBadgeConfig = useCallback((noteKey) => {
    const config = {
      PAYMENT_INCOMPLETE: { icon: '!', tone: 'error' },
      PAYMENT_CANCELED: { icon: '!', tone: 'error' },
      PAYMENT_FAILED: { icon: '!', tone: 'error' },
      CUSTOMER_ACTION: { icon: '!', tone: 'error' },
      PAYMENT_UNCONFIRMED: { icon: '!', tone: 'error' },
      PAYMENT_UNKNOWN: { icon: '!', tone: 'error' },
      MISSING_TRANSFER_NOTE: { icon: '!', tone: 'warning' },
      TERMINAL_WARNING: { icon: '!', tone: 'warning' },
      TERMINAL_NON_FINAL: { icon: '!', tone: 'warning' },
      TERMINAL_MISSING: { icon: '!', tone: 'warning' },
      CUSTOMER_TIMEOUT: { icon: 'i', tone: 'info' },
      CUSTOMER_CANCELED: { icon: 'i', tone: 'info' },
      STRIPE_PENDING: { icon: 'i', tone: 'info' },
    };

    return config[noteKey] || null;
  }, []);

  // --- Client-side filtering on top of server results ---
  const getDisplayedTransactions = useCallback(() => {
    let txs = transactions;
    if (paymentProblemFilter) txs = txs.filter(isPaymentProblem);
    if (billingProblemFilter) txs = txs.filter(isBillingProblem);
    if (customerAbortFilter) txs = txs.filter(isCustomerAbortCase);
    if (toReviewFilter) txs = txs.filter(isToReview);
    return txs;
  }, [transactions, paymentProblemFilter, billingProblemFilter, customerAbortFilter, toReviewFilter, isPaymentProblem, isBillingProblem, isCustomerAbortCase, isToReview]);

  // --- Export CSV ---
  const exportCSV = async () => {
    setExportLoading(true);
    try {
      const data = await tipTransactionService.getTransactions({
        ...filters,
        page: 1,
        pageSize: 500,
        summary: false
      });
      const rows = data.data || [];

      const headers = [
        'ID Transazione', 'Stripe Payment Intent ID', 'Data/Ora',
        'Tenant', 'Venue', 'Paese Carta', 'Canale', 'Tip Link',
        'Importo', 'Fee SoonTip', 'Fee Stripe',
        'Stripe Status Tecnico', 'Terminal Outcome Tecnico',
        'N. Transfer', 'Stato pagamento', 'Transfer', 'Destinatario', 'Distribuzione', 'Fatturazione', 'Note', 'Invoice N.'
      ];

      const csvRows = rows.map(tx => {
        const totalTransfers = tx.tipTransfers?.length || 0;
        const feeSoontip = tx.feeSummary?.feeSoontip ?? 0;
        const feeStripe = tx.feeSummary?.feeStripe ?? 0;
        const invoiceNums = tx.tipTransfers
          ?.filter(t => t.workerInvoice?.invoiceNumber)
          .map(t => t.workerInvoice.invoiceNumber)
          .join('; ') || '';

        const paymentStatus = derivePaymentStatus(tx);
        const transferState = deriveTransferState(tx);
        const recipientState = deriveRecipientState(tx);
        const distributionState = deriveDistributionState(tx);
        const billingState = deriveBillingState(tx);
        const noteState = deriveNoteState(tx);
        const channelState = deriveChannelState(tx);

        const paymentLabel = paymentStatus === 'COLLECTED' ? 'Incassato'
          : paymentStatus === 'MISSING_TRANSFER' ? 'Transfer mancante'
            : paymentStatus === 'PENDING' ? 'In attesa'
              : 'Non incassato';

        return [
          tx.id,
          tx.stripePaymentIntentId || '',
          new Date(tx.transactionDate).toLocaleString('it-IT'),
          tx.venue?.tenant?.company_name || '',
          tx.venue?.name || '',
          tx.cardCountry || 'N/D',
          channelState.label,
          tx.tipLink?.name || '',
          String(tx.amount).replace('.', ','),
          (feeSoontip / 100).toFixed(2).replace('.', ','),
          (feeStripe / 100).toFixed(2).replace('.', ','),
          tx.status || '',
          tx.terminalOutcome || '',
          totalTransfers,
          paymentLabel,
          transferState.label,
          recipientState.label,
          distributionState.label,
          billingState.label,
          noteState.label,
          invoiceNums
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';');
      });

      const BOM = '\uFEFF';
      const csvContent = BOM + [headers.map(h => `"${h}"`).join(';'), ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transazioni_mancia_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Errore export CSV: ${err.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  // --- Clipboard copy ---
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(id);
      setTimeout(() => setCopyFeedback(null), 1500);
    });
  };

  const openDetailModal = async (transaction) => {
    try {
      const response = await tipTransactionService.getTransaction(transaction.id);
      setSelectedTransaction(response.data);
      setDetailModalOpen(true);
    } catch (err) {
      alert(`Errore caricamento dettaglio: ${err.message}`);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTransaction(null);
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatAmount = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const renderCountryOrigin = (countryCode) => {
    const normalized = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : '';
    const validIso2 = /^[A-Z]{2}$/.test(normalized);

    if (!validIso2) {
      return (
        <span className="country-origin country-origin--fallback">
          <span aria-hidden="true">🌐</span>
          <span>nd</span>
        </span>
      );
    }

    const flagCode = normalized.toLowerCase();
    const localFlagSrc = `/flags/${flagCode}.png`;
    const cdnFlagSrc = `https://flagcdn.com/24x18/${flagCode}.png`;

    return (
      <span className="country-origin">
        <img
          src={localFlagSrc}
          alt={normalized}
          className="country-origin__flag"
          loading="lazy"
          onError={(event) => {
            const img = event.currentTarget;

            if (img.dataset.fallback !== 'cdn') {
              img.dataset.fallback = 'cdn';
              img.src = cdnFlagSrc;
              return;
            }

            img.style.display = 'none';
          }}
        />
        <span>{normalized}</span>
      </span>
    );
  };

  const getSoontipFeeTooltip = (transaction, feeConfig, currency = 'EUR') => {
    if (transaction?.soontipFeePercentApplied != null || transaction?.soontipFeeFixedApplied != null) {
      const appliedPercent = transaction.soontipFeePercentApplied ?? 0;
      const appliedFixedCents = transaction.soontipFeeFixedApplied ?? 0;
      const appliedFixedAmount = formatAmount(appliedFixedCents / 100, currency);
      const appliedSource = transaction.soontipFeeSourceApplied
        ? ` (${getFeeSourceLabel(transaction.soontipFeeSourceApplied)})`
        : '';
      return `Fee storica applicata: ${appliedPercent}% + ${appliedFixedAmount}${appliedSource}`;
    }

    if (!feeConfig) {
      return 'Fee storica non presente.\nConfigurazione fee corrente non disponibile.';
    }

    const percent = feeConfig.soontipFeePercent ?? 0;
    const fixedCents = feeConfig.soontipFeeFixedCents ?? 0;
    const fixedAmount = formatAmount(fixedCents / 100, currency);
    const sourceLabel = getFeeSourceLabel(feeConfig.source);

    return `Fee storica non presente.\nFee corrente per venue: ${percent}% + ${fixedAmount} (${sourceLabel})`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'succeeded': { color: '#28a745', label: 'succeeded' },
      'pending': { color: '#ffc107', label: 'pending' },
      'failed': { color: '#dc3545', label: 'failed' },
      'payment_failed': { color: '#dc3545', label: 'payment_failed' },
      'canceled': { color: '#6c757d', label: 'canceled' },
      'requires_payment_method': { color: '#ffc107', label: 'requires_payment_method' },
      'requires_confirmation': { color: '#ffc107', label: 'requires_confirmation' },
      'requires_action': { color: '#ffc107', label: 'requires_action' },
      'processing': { color: '#17a2b8', label: 'processing' },
    };
    const config = statusConfig[status] || { color: '#6c757d', label: status ? status.slice(0, 12) : '-' };
    return (
      <span className="tx-compact-badge" style={{
        backgroundColor: config.color,
        color: 'white',
        padding: '3px 7px',
        borderRadius: '9px',
        fontSize: '10px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        maxWidth: '170px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'inline-block'
      }} title={status}>
        {config.label}
      </span>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const getInvoiceStatusBadge = (status) => {
    if (!status) {
      return <span style={{ color: '#6c757d', fontSize: '12px' }}>-</span>;
    }

    const statusConfig = {
      'created': { color: '#17a2b8', label: 'Creata' },
      'sent': { color: '#28a745', label: 'Inviata' },
      'delivered': { color: '#20c997', label: 'Consegnata' },
      'not-delivered': { color: '#fd7e14', label: 'Messa a disposizione' },
      'not_delivered': { color: '#fd7e14', label: 'Messa a disposizione' },
      'impossible_to_deliver': { color: '#fd7e14', label: 'Messa a disposizione' },
      'rejected': { color: '#dc3545', label: 'Rifiutata' },
      'error': { color: '#e83e8c', label: 'Errore' }
    };

    const config = statusConfig[status] || { color: '#6c757d', label: status };

    return (
      <span className="tx-compact-badge" style={{
        backgroundColor: config.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap'
      }}>
        {config.label}
      </span>
    );
  };

  const getTerminalOutcomeBadge = (terminalOutcome) => {
    if (!terminalOutcome) {
      return <span style={{ color: '#6c757d', fontSize: '12px' }}>-</span>;
    }

    const configByOutcome = {
      SUCCEEDED: { color: '#28a745', title: 'Terminal flow completed successfully' },
      CONFIRM_FAILED: { color: '#dc3545', title: 'PaymentIntent confirmation failed on terminal' },
      CONFIRM_STARTED: { color: '#17a2b8', title: 'Terminal started confirming the PaymentIntent' },
      COLLECT_DECLINED: { color: '#dc3545', title: 'Card was declined during collect' },
      COLLECT_TIMEOUT: { color: '#fd7e14', title: 'Collect timed out on terminal' },
      COLLECT_CANCELED: { color: '#6c757d', title: 'Collect was canceled on terminal' },
      COLLECT_FAILED: { color: '#dc3545', title: 'Collect failed on terminal' },
      COLLECT_COMPLETED: { color: '#20c997', title: 'Collect completed on terminal' },
      COLLECT_STARTED: { color: '#17a2b8', title: 'Terminal started collecting payment method' },
      RETRIEVED: { color: '#6f42c1', title: 'PaymentIntent retrieved on kiosk' },
      CREATED: { color: '#6f42c1', title: 'PaymentIntent created by kiosk' }
    };

    const config = configByOutcome[terminalOutcome] || { color: '#6c757d', title: terminalOutcome };

    return (
      <span className="tx-compact-badge" style={{
        backgroundColor: config.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap'
      }} title={config.title}>
        {terminalOutcome}
      </span>
    );
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="tip-transactions-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Caricamento transazioni...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tip-transactions-container">
        <div className="error-state">
          <h3>❌ Errore</h3>
          <p>{error}</p>
          <button onClick={loadTransactions} className="btn-primary">
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tip-transactions-container">
      {/* Header */}
      <div className="transactions-page-header">
        <span className="transactions-page-header-icon">
          <Receipt size={21} aria-hidden="true" />
        </span>
        <div className="transactions-page-header-copy">
          <h1>Transazioni Mancia</h1>
          <p className="subtitle">Monitoraggio pagamenti, transfer e fatturazione A-Cube</p>
        </div>
        <div className="transactions-page-header-action">
          <TipTransactionsGuide />
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (() => {
        const statusBreakdown = summary.statusBreakdown || {};
        const paymentStatusBreakdown = summary.paymentStatusBreakdown || {};
        const succeeded = paymentStatusBreakdown.COLLECTED ?? statusBreakdown.succeeded ?? 0;
        const pending = paymentStatusBreakdown.PENDING
          ?? ((statusBreakdown.pending || 0) + (statusBreakdown.processing || 0));
        const nonCollected = paymentStatusBreakdown.NOT_COLLECTED
          ?? Object.entries(statusBreakdown).reduce((acc, [status, count]) => {
            if (status === 'succeeded' || status === 'pending' || status === 'processing') return acc;
            return acc + count;
          }, 0);
        const displayedTxs = transactions;
        const missingTransfers = paymentStatusBreakdown.MISSING_TRANSFER
          ?? displayedTxs.filter(tx => derivePaymentStatus(tx) === 'MISSING_TRANSFER').length;
        const billingProblems = displayedTxs.filter(isBillingProblem).length;
        const toReview = displayedTxs.filter(isToReview).length;

        return (
          <div className="summary-cards">
            <div className="summary-card" title="Totale transazioni nel filtro corrente">
              <SummaryLabel icon={ListChecks}>Totale</SummaryLabel>
              <div className="summary-value">{summary.totalTransactions}</div>
            </div>
            <div className="summary-card" title="Importo delle mance riuscite nel filtro corrente">
              <SummaryLabel icon={CircleDollarSign}>Importo mance riuscite</SummaryLabel>
              <div className="summary-value">{formatAmount(summary.successfulAmount ?? summary.totalAmount)}</div>
            </div>
            <div
              className={`summary-card summary-card--clickable summary-card--success${filters.paymentStatus === 'COLLECTED' ? ' summary-card--active' : ''}`}
              onClick={() => handleFilterChange('paymentStatus', filters.paymentStatus === 'COLLECTED' ? '' : 'COLLECTED')}
              title="Filtra transazioni riuscite"
              aria-pressed={filters.paymentStatus === 'COLLECTED'}
            >
              <SummaryLabel icon={CheckCircle2}>Riuscite</SummaryLabel>
              <div className="summary-value">{succeeded}</div>
              {filters.paymentStatus === 'COLLECTED' && <ActiveFilterIndicator />}
            </div>
            <div
              className={`summary-card summary-card--clickable summary-card--danger${filters.paymentStatus === 'NOT_COLLECTED' ? ' summary-card--active' : ''}`}
              onClick={() => handleFilterChange('paymentStatus', filters.paymentStatus === 'NOT_COLLECTED' ? '' : 'NOT_COLLECTED')}
              title="Filtra transazioni non incassate"
              aria-pressed={filters.paymentStatus === 'NOT_COLLECTED'}
            >
              <SummaryLabel icon={XCircle}>Non incassate</SummaryLabel>
              <div className="summary-value">{nonCollected}</div>
              {filters.paymentStatus === 'NOT_COLLECTED' && <ActiveFilterIndicator />}
            </div>
            <div
              className={`summary-card summary-card--clickable summary-card--pending${filters.paymentStatus === 'PENDING' ? ' summary-card--active' : ''}`}
              onClick={() => handleFilterChange('paymentStatus', filters.paymentStatus === 'PENDING' ? '' : 'PENDING')}
              title="Filtra transazioni in attesa"
              aria-pressed={filters.paymentStatus === 'PENDING'}
            >
              <SummaryLabel icon={Clock3}>In attesa</SummaryLabel>
              <div className="summary-value">{pending}</div>
              {filters.paymentStatus === 'PENDING' && <ActiveFilterIndicator />}
            </div>
            <div
              className={`summary-card summary-card--clickable summary-card--warning${filters.paymentStatus === 'MISSING_TRANSFER' ? ' summary-card--active' : ''}`}
              onClick={() => handleFilterChange('paymentStatus', filters.paymentStatus === 'MISSING_TRANSFER' ? '' : 'MISSING_TRANSFER')}
              title="Filtra tutte le transazioni riuscite senza transfer"
              aria-pressed={filters.paymentStatus === 'MISSING_TRANSFER'}
            >
              <SummaryLabel icon={Split}>Transfer mancanti</SummaryLabel>
              <div className="summary-value">{missingTransfers}</div>
              {filters.paymentStatus === 'MISSING_TRANSFER' && <ActiveFilterIndicator />}
            </div>
            <div
              className={`summary-card summary-card--clickable summary-card--billing${billingProblemFilter ? ' summary-card--active' : ''}`}
              onClick={() => setBillingProblemFilter(v => !v)}
              title="Problemi fatturazione (pagina corrente)"
              aria-pressed={billingProblemFilter}
            >
              <SummaryLabel icon={FileWarning}>Problemi fatturazione</SummaryLabel>
              <div className="summary-value">{billingProblems}</div>
              <div className="summary-sublabel">pag. corrente</div>
              {billingProblemFilter && <ActiveFilterIndicator />}
            </div>
            <div
              className={`summary-card summary-card--clickable summary-card--warning${toReviewFilter ? ' summary-card--active' : ''}`}
              onClick={() => setToReviewFilter(v => !v)}
              title="Anomalie operative in note (pagina corrente)"
              aria-pressed={toReviewFilter}
            >
              <SummaryLabel icon={SearchCheck}>Anomalie operative</SummaryLabel>
              <div className="summary-value">{toReview}</div>
              <div className="summary-sublabel">pag. corrente</div>
              {toReviewFilter && <ActiveFilterIndicator />}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="filters-section">
        <div className="transaction-filter-toolbar">
          <div className="transaction-filter-search">
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              placeholder="Cerca ID transazione, Stripe Payment ID, venue o tenant..."
              value={filters.q}
              onChange={(e) => handleFilterChange('q', e.target.value)}
              aria-label="Cerca transazioni"
            />
            {filters.q && (
              <button
                type="button"
                className="transaction-filter-search-clear"
                onClick={() => handleFilterChange('q', '')}
                title="Cancella ricerca"
                aria-label="Cancella ricerca"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <button
            type="button"
            className="transaction-filter-toggle"
            onClick={() => setFiltersExpanded(value => !value)}
            aria-expanded={filtersExpanded}
          >
            <Filter size={16} aria-hidden="true" />
            <span>Filtri</span>
            {activeFilterCount > 0 && (
              <span className="transaction-filter-badge">{activeFilterCount}</span>
            )}
          </button>

          <div className="transaction-filter-results">
            <span>{loading ? 'Caricamento...' : `${totalItems} ${totalItems === 1 ? 'risultato' : 'risultati'}`}</span>
            <button onClick={exportCSV} className="btn-export btn-export--primary" disabled={exportLoading}>
              <FileDown size={14} aria-hidden="true" />
              {exportLoading ? 'Export...' : 'Excel'}
            </button>
          </div>
        </div>

        {/* Quick date presets */}
        <div className="date-preset-bar">
          {[
            { key: 'all', label: 'Tutti' },
            { key: 'today', label: 'Oggi' },
            { key: 'yesterday', label: 'Ieri' },
            { key: '7d', label: 'Ultimi 7 gg' },
            { key: '30d', label: 'Ultimi 30 gg' },
            { key: 'this_month', label: 'Questo mese' },
            { key: 'last_month', label: 'Mese scorso' },
            { key: 'custom', label: 'Custom' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`date-preset-btn${datePreset === key ? ' date-preset-btn--active' : ''}`}
              onClick={() => applyDatePreset(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {filtersExpanded && (
          <div className="transaction-filters-panel">
        {(datePreset !== 'all' || filters.paymentStatus) && (
          <div className="applied-filters-bar" aria-live="polite">
            <span className="applied-filters-label">Vista attiva</span>
            {datePreset !== 'all' && (
              <span className="applied-filter-chip">
                Periodo: {DATE_PRESET_LABELS[datePreset] || 'Personalizzato'}
              </span>
            )}
            {filters.paymentStatus && (
              <span className="applied-filter-chip applied-filter-chip--success">
                Stato: {PAYMENT_STATUS_LABELS[filters.paymentStatus] || filters.paymentStatus}
              </span>
            )}
          </div>
        )}
        <div className="filters-row filters-row--primary">
          <div className="filter-group">
            <label>Da:</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>A:</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Tenant:</label>
            <select
              value={filters.tenantId}
              onChange={(e) => handleFilterChange('tenantId', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti i tenant</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>{tenant.company_name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Venue:</label>
            <select
              value={filters.venueId}
              onChange={(e) => handleFilterChange('venueId', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti i locali</option>
              {venues.map(venue => (
                <option key={venue.id} value={venue.id}>{venue.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Stato pagamento:</label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti gli stati</option>
              <option value="COLLECTED">Riuscite</option>
              <option value="NOT_COLLECTED">Non incassate</option>
              <option value="PENDING">In attesa</option>
              <option value="MISSING_TRANSFER">Riuscite senza transfer</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Destinatario:</label>
            <select
              value={filters.recipientMode}
              onChange={(e) => handleFilterChange('recipientMode', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti i destinatari</option>
              <option value="STAFF">Staff del locale</option>
              <option value="NAMED_SINGLE">Percettore selezionato</option>
            </select>
          </div>
        </div>

        <div className="filters-row filters-row--secondary">
          <div className="filter-group">
            <label>Terminal:</label>
            <select
              value={filters.terminalOutcome}
              onChange={(e) => handleFilterChange('terminalOutcome', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti gli esiti</option>
              <option value="NONE">Nessun esito registrato</option>
              <option value="SUCCEEDED">Pagamento completato</option>
              <option value="CONFIRM_FAILED">Conferma fallita</option>
              <option value="COLLECT_DECLINED">Carta rifiutata</option>
              <option value="COLLECT_TIMEOUT">Lettura carta scaduta</option>
              <option value="COLLECT_CANCELED">Operazione annullata</option>
              <option value="COLLECT_FAILED">Lettura carta fallita</option>
              <option value="COLLECT_COMPLETED">Carta acquisita</option>
              <option value="COLLECT_STARTED">Lettura carta in corso</option>
              <option value="CONFIRM_STARTED">Conferma in corso</option>
              <option value="RETRIEVED">Pagamento recuperato</option>
              <option value="CREATED">Pagamento creato</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Paese carta (ISO2):</label>
            <input
              type="text"
              placeholder="Es. IT"
              value={filters.cardCountry}
              onChange={(e) => handleFilterChange('cardCountry', e.target.value.toUpperCase())}
              className="filter-input filter-input--narrow"
              maxLength={2}
            />
          </div>
          <div className="filter-group">
            <label>Distribuzione:</label>
            <select
              value={filters.distributionMode}
              onChange={(e) => handleFilterChange('distributionMode', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutte le modalità</option>
              <option value="STANDARD">Standard</option>
              <option value="WORKER_PERCENTAGE">Percentuale per percettore</option>
              <option value="TEAM_PERCENTAGE">Percentuale per team</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Canale:</label>
            <select
              value={filters.collectionChannel}
              onChange={(e) => handleFilterChange('collectionChannel', e.target.value)}
              className="filter-input"
            >
              <option value="">Tutti i canali</option>
              <option value="KIOSK_TERMINAL">Totem con terminale</option>
              <option value="TIP_LINK_WEB">Tip Link web</option>
            </select>
          </div>
        </div>

        <div className="filter-actions-bar">
          <div className="filter-quick-filters">
            <button
              className={`quick-filter-btn${paymentProblemFilter ? ' quick-filter-btn--active' : ''}`}
              onClick={() => setPaymentProblemFilter(v => !v)}
            >
              <AlertTriangle size={14} aria-hidden="true" />
              Problemi pagamento
            </button>
            <button
              className={`quick-filter-btn${billingProblemFilter ? ' quick-filter-btn--active' : ''}`}
              onClick={() => setBillingProblemFilter(v => !v)}
            >
              <Receipt size={14} aria-hidden="true" />
              Problemi fatturazione
            </button>
            <button
              className={`quick-filter-btn${customerAbortFilter ? ' quick-filter-btn--active' : ''}`}
              onClick={() => setCustomerAbortFilter(v => !v)}
            >
              <UserX size={14} aria-hidden="true" />
              Cliente non completa
            </button>
            <button
              className={`quick-filter-btn${toReviewFilter ? ' quick-filter-btn--active' : ''}`}
              onClick={() => setToReviewFilter(v => !v)}
            >
              <SearchCheck size={14} aria-hidden="true" />
              Anomalie operative
            </button>
          </div>
          <div className="filter-actions">
            <button onClick={resetFilters} className="btn-secondary">
              <RefreshCcw size={14} aria-hidden="true" />
              Reset
            </button>
          </div>
        </div>

        {(paymentProblemFilter || billingProblemFilter || customerAbortFilter || toReviewFilter) && (
          <div className="filters-active-notice">
            Filtri rapidi attivi applicati alla pagina corrente.{' '}
            {totalItems > pageSize && (
              <span>Per coprire tutti i {totalItems} risultati aumenta la dimensione pagina.</span>
            )}
          </div>
        )}
          </div>
        )}
      </div>

      {/* Table */}
      <TableZoomControls {...tableZoom} />
      <div className="table-container table-scroll-frame">
        <div className="table-toolbar">
          <span className="table-count">
            {loading ? 'Caricamento...' : `${getDisplayedTransactions().length} transazioni mostrate`}
            {totalItems > 0 && ` (${totalItems} totali)`}
          </span>
          <div className="page-size-selector">
            <label>Righe:</label>
            <select value={pageSize} onChange={(e) => handlePageSizeChange(e.target.value)}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <table
          className="admin-table devices-table transactions-table mobile-zoom-table"
          style={{ '--mobile-table-zoom': `${tableZoom.zoom}%` }}
        >
          <thead>
            <tr>
              <th><TransactionColumnLabel icon={CalendarDays}>Data</TransactionColumnLabel></th>
              <th><TransactionColumnLabel icon={Store}>Venue / Tenant</TransactionColumnLabel></th>
              <th className="col-right"><TransactionColumnLabel icon={CircleDollarSign}>Importo</TransactionColumnLabel></th>
              <th className="col-right"><TransactionColumnLabel icon={BadgeDollarSign}>Fee</TransactionColumnLabel></th>
              <th>
                <span className="transaction-heading-with-info">
                  <TransactionColumnLabel icon={CreditCard}>Stato</TransactionColumnLabel>
                  <span className="info-icon" title="Stato economico del pagamento derivato da Stripe status e presenza transfer.">
                    <svg className="info-icon__svg" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-1-9h2v6h-2v-6zm0-4h2v2h-2V7z" /></svg>
                  </span>
                </span>
              </th>
              <th><TransactionColumnLabel icon={ArrowRightLeft}>Transfer</TransactionColumnLabel></th>
              <th><TransactionColumnLabel icon={Users}>Dest./Distr.</TransactionColumnLabel></th>
              <th><TransactionColumnLabel icon={Receipt}>Fatturazione</TransactionColumnLabel></th>
              <th>
                <span className="transaction-heading-with-info">
                  <TransactionColumnLabel icon={StickyNote}>Note</TransactionColumnLabel>
                  <span className="info-icon" title="Note operative business-friendly (terminale, stati non finali, outcome mancanti).">
                    <svg className="info-icon__svg" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-1-9h2v6h-2v-6zm0-4h2v2h-2V7z" /></svg>
                  </span>
                </span>
              </th>
              <th className="col-center"><TransactionColumnLabel icon={Settings}>Azioni</TransactionColumnLabel></th>
            </tr>
          </thead>
          <tbody>
            {loading && transactions.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{ margin: '0 auto 10px' }}></div>
                  Caricamento...
                </td>
              </tr>
            ) : getDisplayedTransactions().length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Nessuna transazione trovata
                </td>
              </tr>
            ) : (
              getDisplayedTransactions().map((tx) => {
                const totalTransfers = tx.tipTransfers?.length || 0;
                const transferState = deriveTransferState(tx);
                const recipientState = deriveRecipientState(tx);
                const distributionState = deriveDistributionState(tx);
                const billingState = deriveBillingState(tx);
                const noteState = deriveNoteState(tx);
                const noteBadgeConfig = getNoteBadgeConfig(noteState.key);

                const feeStripeCents = tx.feeSummary?.feeStripe ?? (tx.tipTransfers || []).reduce((sum, t) => sum + (t.feeStripe || 0), 0);
                const feeSoontipCents = tx.feeSummary?.feeSoontip ?? (tx.tipTransfers || []).reduce((sum, t) => sum + (t.feeSoontip || 0), 0);
                const feeTotalCents = tx.feeSummary?.feeTotal ?? (tx.tipTransfers || []).reduce((sum, t) => sum + (t.feeTotal || 0), 0);
                const hasFeeData = feeTotalCents > 0 || feeSoontipCents > 0 || feeStripeCents > 0;
                const anomaly = isPaymentProblem(tx);
                const channelState = deriveChannelState(tx);

                return (
                  <tr
                    key={tx.id}
                    className={anomaly ? 'row-anomaly' : ''}
                    onClick={() => openDetailModal(tx)}
                    style={{ cursor: 'pointer' }}
                    title="Clicca per vedere il dettaglio"
                  >
                    <td className="col-date">
                      {formatDate(tx.transactionDate)}
                      {tx.cardCountry && (
                        <div style={{ marginTop: '3px' }}>{renderCountryOrigin(tx.cardCountry)}</div>
                      )}
                    </td>
                    <td>
                      <div>{tx.venue?.name || <span style={{ color: '#999' }}>N/D</span>}</div>
                      {tx.venue?.tenant && (
                        <div className="tenant-name">{tx.venue.tenant.company_name}</div>
                      )}
                      <div style={{ marginTop: '3px', display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span
                          className="tx-compact-badge"
                          style={{ backgroundColor: channelState.bg, color: channelState.color }}
                          title={tx.tipLink?.name ? `Tip Link: ${tx.tipLink.name}` : (tx.device?.label ? `Totem: ${tx.device.label}` : undefined)}
                        >
                          {channelState.icon} {channelState.label}
                        </span>
                        {(() => {
                          // Use snapshot fields (authoritative at payment time), fall back to current device/tipLink
                          const aType = tx.assignmentTypeSnapshot || tx.device?.assignment_type || tx.tipLink?.assignmentType;
                          if (!aType) return null;
                          const isTeam = aType === 'TEAM';
                          // teamSnapshotNames resolved server-side from snapshot IDs (stable across reassignments)
                          const teamNames = tx.teamSnapshotNames || [];
                          const tooltip = isTeam
                            ? (teamNames.length > 0 ? `Team: ${teamNames.join(', ')}` : 'Team specifico')
                            : 'Pool globale venue';
                          return (
                            <span
                              className="tx-compact-badge"
                              style={{ backgroundColor: isTeam ? '#0d6efd' : '#6c757d', color: 'white', fontSize: '11px' }}
                              title={tooltip}
                            >
                              {isTeam ? '👥 Team' : '🌐 Pool'}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="col-right">{formatAmount(tx.amount, tx.currency)}</td>
                    <td className="col-right">
                      {hasFeeData ? (
                        <div className="tx-fee-cell-wrapper">
                          <div className="tx-fee-total">{formatAmount(feeTotalCents / 100, tx.currency)}</div>
                          <div className="tx-fee-breakdown">
                            <span title={getSoontipFeeTooltip(tx, tx.feeConfig, tx.currency)}>
                              S: {formatAmount(feeSoontipCents / 100, tx.currency)}
                            </span>
                            <span>Stripe: {formatAmount(feeStripeCents / 100, tx.currency)}</span>
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td>
                      {getPaymentStatusBadge(tx)}
                    </td>
                    <td>
                      <span className="tx-compact-badge" style={{ backgroundColor: transferState.bg, color: transferState.color }} title={transferState.title || `Transfer registrati: ${totalTransfers}`}>
                        {transferState.label}
                        {transferState.marker ? <span style={{ marginLeft: 6 }}>{transferState.marker}</span> : null}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span
                          className="tx-compact-badge"
                          style={{ backgroundColor: recipientState.bg, color: recipientState.color }}
                          title={recipientState.key === 'NAMED_SINGLE' ? 'Un solo worker scelto' : 'Tutto lo staff'}
                        >
                          {recipientState.label}
                        </span>
                        <span
                          className="tx-compact-badge"
                          style={{ backgroundColor: distributionState.bg, color: distributionState.color }}
                          title={distributionState.key === 'STANDARD' ? 'Riparto uguale per tutti' : 'Riparto per percentuali configurate'}
                        >
                          {distributionState.label}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="tx-compact-badge" style={{ backgroundColor: billingState.bg, color: billingState.color }}>
                        {billingState.label}
                      </span>
                    </td>
                    <td title={noteState.detail || undefined}>
                      {noteBadgeConfig ? (
                        <span className="tx-note-alert-inline">
                          <span className={`tx-note-alert-badge tx-note-alert-badge--${noteBadgeConfig.tone}`}>{noteBadgeConfig.icon}</span>
                          <span className="tx-note-text">{noteState.label}</span>
                        </span>
                      ) : (
                        <span className={`tx-note-text${noteState.label === '—' ? ' tx-note-text--muted' : ''}`}>
                          {noteState.label}
                        </span>
                      )}
                    </td>
                    <td className="col-center" onClick={(e) => e.stopPropagation()}>
                      <div className="kebab-container" ref={openKebabId === tx.id ? kebabRef : null}>
                        <button
                          className="kebab-btn"
                          onClick={() => setOpenKebabId(openKebabId === tx.id ? null : tx.id)}
                          title="Azioni"
                        >
                          ⋯
                        </button>
                        {openKebabId === tx.id && (
                          <div className="kebab-menu">
                            <button className="kebab-menu-item" onClick={() => { setOpenKebabId(null); openDetailModal(tx); }}>
                              📄 Vedi dettagli
                            </button>
                            {totalTransfers > 0 && (
                              <button className="kebab-menu-item" onClick={() => { setOpenKebabId(null); window.location.href = `/admin/tip-transactions/${tx.id}/transfers`; }}>
                                🔀 Vedi transfer ({totalTransfers})
                              </button>
                            )}
                            {tx.stripePaymentIntentId && (
                              <button
                                className="kebab-menu-item"
                                onClick={() => { setOpenKebabId(null); copyToClipboard(tx.stripePaymentIntentId, `stripe-${tx.id}`); }}
                              >
                                {copyFeedback === `stripe-${tx.id}` ? '✅ Copiato!' : '📋 Copia Stripe ID'}
                              </button>
                            )}
                            <button
                              className="kebab-menu-item"
                              onClick={() => { setOpenKebabId(null); copyToClipboard(tx.id, `id-${tx.id}`); }}
                            >
                              {copyFeedback === `id-${tx.id}` ? '✅ Copiato!' : '🔗 Copia ID Transazione'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedTransaction && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📄 Dettaglio Transazione</h2>
              <button onClick={closeDetailModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Informazioni Generali</h3>
                <div className="detail-grid">
                  <div className="detail-item detail-item-full">
                    <strong>ID Transazione:</strong>
                    <span className="mono-text">{selectedTransaction.id}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Data:</strong>
                    <span>{formatDate(selectedTransaction.transactionDate)}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Importo:</strong>
                    <span>{formatAmount(selectedTransaction.amount, selectedTransaction.currency)}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Stato pagamento:</strong>
                    {getPaymentStatusBadge(selectedTransaction)}
                  </div>
                  <div className="detail-item">
                    <strong>Destinatario:</strong>
                    <span>{deriveRecipientState(selectedTransaction).label}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Distribuzione:</strong>
                    <span>{deriveDistributionState(selectedTransaction).label}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Canale:</strong>
                    <span
                      className="tx-compact-badge"
                      style={{ backgroundColor: deriveChannelState(selectedTransaction).bg, color: deriveChannelState(selectedTransaction).color }}
                    >
                      {deriveChannelState(selectedTransaction).icon} {deriveChannelState(selectedTransaction).label}
                    </span>
                  </div>
                  {selectedTransaction.tipLink && (
                    <div className="detail-item">
                      <strong>Tip Link:</strong>
                      <span>{selectedTransaction.tipLink.name}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <strong>Valuta:</strong>
                    <span>{selectedTransaction.currency?.toUpperCase() || 'EUR'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Stripe</h3>
                <div className="detail-grid">
                  <div className="detail-item detail-item-full">
                    <strong>Payment Intent ID:</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="mono-text">{selectedTransaction.stripePaymentIntentId || '-'}</span>
                      {selectedTransaction.stripePaymentIntentId && (
                        <button
                          className="btn-copy-inline"
                          onClick={() => copyToClipboard(selectedTransaction.stripePaymentIntentId, 'modal-stripe')}
                        >
                          {copyFeedback === 'modal-stripe' ? '✅' : '📋'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="detail-item">
                    <strong>Stripe status tecnico:</strong>
                    {getStatusBadge(selectedTransaction.status)}
                  </div>
                  <div className="detail-item">
                    <strong>Origine Carta:</strong>
                    {renderCountryOrigin(selectedTransaction.cardCountry)}
                  </div>
                </div>
              </div>

              {selectedTransaction.collectionChannel === 'TIP_LINK_WEB' ? (
                <div className="detail-section">
                  <h3>Web Checkout</h3>
                  <div className="detail-grid">
                    <div className="detail-item detail-item-full">
                      <span style={{ color: '#6c757d' }}>
                        Pagamento raccolto tramite Tip Link (checkout web) - nessun terminale fisico coinvolto.
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="detail-section">
                  <h3>Terminal</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Outcome:</strong>
                      {getTerminalOutcomeBadge(selectedTransaction.terminalOutcome)}
                    </div>
                    <div className="detail-item">
                      <strong>Outcome At:</strong>
                      <span>{selectedTransaction.terminalOutcomeAt ? formatDate(selectedTransaction.terminalOutcomeAt) : '-'}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Outcome Code:</strong>
                      <span className="mono-text">{selectedTransaction.terminalOutcomeCode || '-'}</span>
                    </div>
                    <div className="detail-item detail-item-full">
                      <strong>Outcome Message:</strong>
                      <span>{selectedTransaction.terminalOutcomeMessage || '-'}</span>
                    </div>
                  </div>
                </div>
              )}

              {getLastPaymentErrorDetail(selectedTransaction) && (
                <div className="detail-section">
                  <h3>Errore Pagamento</h3>
                  <div className="detail-grid">
                    <div className="detail-item detail-item-full">
                      <strong>Messaggio:</strong>
                      <span>{getLastPaymentErrorDetail(selectedTransaction).message || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Codice:</strong>
                      <span className="mono-text">{getLastPaymentErrorDetail(selectedTransaction).code || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Decline Code:</strong>
                      <span className="mono-text">{getLastPaymentErrorDetail(selectedTransaction).declineCode || '-'}</span>
                    </div>
                    {getLastPaymentErrorDetail(selectedTransaction).cardBrand && (
                      <div className="detail-item">
                        <strong>Carta:</strong>
                        <span>{getLastPaymentErrorDetail(selectedTransaction).cardBrand} •••• {getLastPaymentErrorDetail(selectedTransaction).cardLast4 || '----'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h3>Venue, Tenant & Device</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Venue:</strong>
                    <span>{selectedTransaction.venue?.name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Tenant:</strong>
                    <span>{selectedTransaction.venue?.tenant?.company_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Device:</strong>
                    <span>{selectedTransaction.device?.label || selectedTransaction.device?.setupCode || '-'}</span>
                  </div>
                </div>
              </div>

              {selectedTransaction.tipTransfers && selectedTransaction.tipTransfers.length > 0 && (
                <div className="detail-section">
                  <h3>Transfer ({selectedTransaction.tipTransfers.length} worker)</h3>
                  {(() => {
                    const netTotal = selectedTransaction.tipTransfers.reduce((sum, t) => sum + (t.amountNet ?? t.amount ?? 0), 0);
                    const feeSoontipTotal = selectedTransaction.tipTransfers.reduce((sum, t) => sum + (t.feeSoontip || 0), 0);
                    const feeStripeTotal = selectedTransaction.tipTransfers.reduce((sum, t) => sum + (t.feeStripe || 0), 0);
                    const cur = selectedTransaction.currency || 'EUR';
                    const fmt = (cents) => (cents / 100).toLocaleString('it-IT', { style: 'currency', currency: cur });
                    const teamAllocs = selectedTransaction.activeRuleTeamAllocations;
                    return (
                      <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#555', lineHeight: 1.7 }}>
                        <strong>Mancia netta da distribuire (dopo le fee):</strong> {fmt(netTotal)}<br />
                        <span style={{ color: '#888' }}>Fee SoonTip: {fmt(feeSoontipTotal)} &nbsp;·&nbsp; Fee Stripe: {fmt(feeStripeTotal)}</span>
                        {teamAllocs && teamAllocs.length > 0 && (
                          <>
                            <br />
                            <span style={{ color: '#888' }}>
                              <strong style={{ color: '#555' }}>Team configurati</strong>{' '}<span style={{ fontSize: '10px', color: '#94a3b8' }}>(snapshot al pagamento)</span>{' '}
                              {teamAllocs.map((a, i) => (
                                <span key={a.teamId}>
                                  {a.nameSnapshot || a.teamId}{' '}
                                  <strong>{(a.configuredPct ?? a.percentage) != null ? `${a.configuredPct ?? a.percentage}%` : 'n.d.'}</strong>
                                  {a.normalizedPct != null && Math.abs(a.normalizedPct - (a.configuredPct ?? a.percentage)) > 0.01 && (
                                    <span style={{ color: '#6366f1' }}> → {a.normalizedPct.toFixed(2)}%</span>
                                  )}
                                  {i < teamAllocs.length - 1 ? ' · ' : ''}
                                </span>
                              ))}
                            </span>
                          </>
                        )}
                      </p>
                    );
                  })()}
                  <table className="transfer-mini-table">
                    <thead>
                      <tr>
                        <th>Worker</th>
                        <th className="col-right">Importo</th>
                        <th className="col-right">Fee S.</th>
                        <th className="col-right">Fee Stripe</th>
                        <th className="col-right">% Applicata</th>
                        <th>Fattura</th>
                        <th>A-Cube</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const isTeamMode = selectedTransaction.distributionSummary === 'TEAM_PERCENTAGE';
                        const allocs = selectedTransaction.activeRuleTeamAllocations || [];
                        const transfers = selectedTransaction.tipTransfers;

                        if (!isTeamMode) {
                          return transfers.map((t) => {
                            const pct = Number(t.appliedPercentage);
                            const pctLabel = Number.isFinite(pct) && pct > 0 ? `${pct.toFixed(2)}%` : '-';
                            return (
                              <tr key={t.id}>
                                <td>
                                  {t.worker ? (
                                    <span>
                                      {t.worker.firstName} {t.worker.lastName}
                                      {t.worker.team && (
                                        <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '1px' }}>
                                          {t.worker.team.name}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="mono-text" style={{ fontSize: '10px' }}>
                                      {t.destination ? t.destination.slice(-8) : '-'}
                                    </span>
                                  )}
                                </td>
                                <td className="col-right">{t.amount != null ? formatAmount(t.amount / 100, selectedTransaction.currency) : '-'}</td>
                                <td className="col-right">{t.feeSoontip != null ? formatAmount(t.feeSoontip / 100, selectedTransaction.currency) : '-'}</td>
                                <td className="col-right">{t.feeStripe != null ? formatAmount(t.feeStripe / 100, selectedTransaction.currency) : '-'}</td>
                                <td className="col-right" title={t.distributionModeApplied || 'STANDARD'}>{pctLabel}</td>
                                <td>{t.workerInvoice?.invoiceNumber ? <span className="mono-text" style={{ fontSize: '10px' }}>{t.workerInvoice.invoiceNumber}</span> : <span style={{ color: '#999' }}>-</span>}</td>
                                <td>{t.workerInvoice?.acubeInvoiceStatus ? getInvoiceStatusBadge(t.workerInvoice.acubeInvoiceStatus) : t.acubeInvoiceStatus ? getInvoiceStatusBadge(t.acubeInvoiceStatus) : <span style={{ color: '#999' }}>-</span>}</td>
                              </tr>
                            );
                          });
                        }

                        // TEAM_PERCENTAGE: build groups from snapshot (historical), fallback to current allocs for empty teams
                        const groups = new Map();

                        // First pass: collect historical group metadata from snapshots
                        transfers.forEach(t => {
                          (t.metadata?.teamSnapshot || []).forEach(s => {
                            if (!groups.has(s.teamId)) {
                              groups.set(s.teamId, {
                                teamId: s.teamId,
                                nameSnapshot: s.nameSnapshot,
                                configuredPct: s.teamConfiguredPct ?? null,
                                normalizedPct: s.teamPct ?? null,
                                transfers: [],
                              });
                            }
                          });
                        });

                        groups.set('__multi__', { teamId: '__multi__', nameSnapshot: 'Multi-team', configuredPct: null, normalizedPct: null, transfers: [] });

                        // Empty teams from historical snapshot (normalizedPct==null means team was empty at payment time)
                        allocs.forEach(a => {
                          if (!groups.has(a.teamId)) {
                            groups.set(a.teamId, {
                              teamId: a.teamId,
                              nameSnapshot: a.nameSnapshot || a.teamId,
                              configuredPct: a.configuredPct ?? a.percentage ?? null,
                              normalizedPct: a.normalizedPct ?? null,
                              transfers: [],
                            });
                          }
                        });

                        transfers.forEach(t => {
                          const snap = t.metadata?.teamSnapshot;
                          if (snap && snap.length > 1) {
                            groups.get('__multi__').transfers.push(t);
                          } else {
                            const tid = snap?.[0]?.teamId || t.worker?.team?.id;
                            if (tid && groups.has(tid)) groups.get(tid).transfers.push(t);
                            else {
                              if (!groups.has('__other__')) groups.set('__other__', { teamId: '__other__', nameSnapshot: 'Altro', configuredPct: null, normalizedPct: null, transfers: [] });
                              groups.get('__other__').transfers.push(t);
                            }
                          }
                        });

                        const multiXfers = groups.get('__multi__')?.transfers || [];
                        groups.forEach((grp, tid) => {
                          if (tid === '__multi__') return;
                          grp.hasMultiTeamWorkers = multiXfers.some(t =>
                            (t.metadata?.teamSnapshot || []).some(s => s.teamId === tid)
                          );
                        });
                        const allGroups = [...groups.values()].filter(g => g.transfers.length > 0 || g.configuredPct != null);

                        return allGroups.flatMap((group) => {
                          const isEmpty = group.transfers.length === 0;
                          const groupNet = group.transfers.reduce((s, t) => s + (t.amountNet ?? t.amount ?? 0), 0);
                          const cur = selectedTransaction.currency || 'EUR';
                          const fmt = (c) => (c / 100).toLocaleString('it-IT', { style: 'currency', currency: cur });
                          const showNormalized = !isEmpty && group.normalizedPct != null && group.configuredPct != null
                            && Math.abs(group.normalizedPct - group.configuredPct) > 0.01;
                          return [
                            <tr key={`gh-${group.teamId}`} style={{ background: isEmpty ? '#f8fafc' : '#eef2ff', opacity: isEmpty ? 0.7 : 1 }}>
                              <td colSpan={7} style={{ padding: '7px 10px', borderBottom: '2px solid #c7d2fe', borderTop: '2px solid #c7d2fe' }}>
                                <strong style={{ color: isEmpty ? '#94a3b8' : '#3730a3', marginRight: 8, textDecoration: isEmpty ? 'line-through' : 'none' }}>{group.nameSnapshot || group.teamId}</strong>
                                {group.configuredPct != null && (
                                  showNormalized ? (
                                    <>
                                      <span style={{ background: '#94a3b8', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, marginRight: 4, textDecoration: 'line-through' }}>{group.configuredPct}%</span>
                                      <span style={{ marginRight: 4, color: '#64748b', fontSize: 11 }}>→</span>
                                      <span style={{ background: '#6366f1', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, marginRight: 8 }}>{group.normalizedPct.toFixed(2)}%</span>
                                    </>
                                  ) : (
                                    <span style={{ background: isEmpty ? '#e2e8f0' : '#6366f1', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, marginRight: 8 }}>{group.configuredPct}%</span>
                                  )
                                )}
                                {isEmpty
                                  ? (group.hasMultiTeamWorkers
                                      ? <span style={{ fontSize: 11, color: '#6366f1', fontStyle: 'italic' }}>↓ worker nel gruppo multi-team</span>
                                      : <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Nessun worker attivo</span>)
                                  : <span style={{ fontSize: 12, color: '#4338ca', fontWeight: 600 }}>{fmt(groupNet)}</span>
                                }
                              </td>
                            </tr>,
                            ...group.transfers.map((t) => {
                              const pct = Number(t.appliedPercentage);
                              const pctLabel = Number.isFinite(pct) && pct > 0 ? `${pct.toFixed(2)}%` : '-';
                              const isMultiTeam = t.metadata?.multiTeam === true;
                              const teamSnap = t.metadata?.teamSnapshot;
                              const teamSnapLabel = isMultiTeam && Array.isArray(teamSnap)
                                ? teamSnap.map(s => s.nameSnapshot).join(' + ')
                                : null;
                              return (
                                <tr key={t.id}>
                                  <td style={{ paddingLeft: 22 }}>
                                    {t.worker ? (
                                      <span>
                                        {t.worker.firstName} {t.worker.lastName}
                                        {isMultiTeam && (
                                          <span style={{ display: 'block', fontSize: '10px', color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 5px', marginTop: 2, width: 'fit-content' }} title={teamSnapLabel || 'Riceve quote da più team'}>
                                            ⚠️ {teamSnapLabel || 'Multi-team'}
                                          </span>
                                        )}
                                        {isMultiTeam && Array.isArray(teamSnap) && teamSnap.length > 0 && (
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                                            {teamSnap.map(s => (
                                              <span key={s.teamId} style={{ fontSize: 10, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', padding: '1px 5px', borderRadius: 4 }}>
                                                {s.nameSnapshot}: <strong>{s.pctSnapshot != null ? `${s.pctSnapshot.toFixed(2)}%` : '-'}</strong>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="mono-text" style={{ fontSize: '10px' }}>{t.destination ? t.destination.slice(-8) : '-'}</span>
                                    )}
                                  </td>
                                  <td className="col-right">{t.amount != null ? formatAmount(t.amount / 100, selectedTransaction.currency) : '-'}</td>
                                  <td className="col-right">{t.feeSoontip != null ? formatAmount(t.feeSoontip / 100, selectedTransaction.currency) : '-'}</td>
                                  <td className="col-right">{t.feeStripe != null ? formatAmount(t.feeStripe / 100, selectedTransaction.currency) : '-'}</td>
                                  <td className="col-right">{pctLabel}</td>
                                  <td>{t.workerInvoice?.invoiceNumber ? <span className="mono-text" style={{ fontSize: '10px' }}>{t.workerInvoice.invoiceNumber}</span> : <span style={{ color: '#999' }}>-</span>}</td>
                                  <td>{t.workerInvoice?.acubeInvoiceStatus ? getInvoiceStatusBadge(t.workerInvoice.acubeInvoiceStatus) : t.acubeInvoiceStatus ? getInvoiceStatusBadge(t.acubeInvoiceStatus) : <span style={{ color: '#999' }}>-</span>}</td>
                                </tr>
                              );
                            }),
                          ];
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
                <div className="detail-section">
                  <h3>Metadata</h3>
                  <pre className="metadata-json">
                    {JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedTransaction.tipTransfers && selectedTransaction.tipTransfers.length > 0 && (
                <button
                  onClick={() => {
                    window.location.href = `/admin/tip-transactions/${selectedTransaction.id}/transfers`;
                  }}
                  className="btn-primary"
                >
                  🔀 Dettaglio Transfer ({selectedTransaction.tipTransfers.length})
                </button>
              )}
              <button onClick={closeDetailModal} className="btn-secondary">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipTransactions;
